import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { securityStatus, unlockVault, unlockWithOtp, setPin as apiSetPin, fetchMe, lockVault, recoverOwnerPin } from "../lib/api";
import { logError } from "../lib/log";

const SecurityContext = createContext(null);
export const useSecurity = () => useContext(SecurityContext);

// The unlock token lives in an HttpOnly cookie set by the server. Because embedded/iframe
// previews block third-party cookies, the token is also kept in memory (never in storage)
// and sent as a header for the lifetime of this tab.
axios.defaults.withCredentials = true;
export const setSessionHeader = (token) => {
  if (token) axios.defaults.headers.common["X-Vault-Token"] = token;
  else delete axios.defaults.headers.common["X-Vault-Token"];
};

const IMPLICIT_OWNER = { id: null, name: "You", role: "owner", role_label: "Owner", color: "#C25E30" };

const useIdleLock = (enabled, minutes, onIdle) => {
  useEffect(() => {
    if (!enabled) return undefined;
    let timer = null;
    const reset = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(onIdle, (minutes || 5) * 60 * 1000);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (timer) clearTimeout(timer);
    };
  }, [enabled, minutes, onIdle]);
};

export const SecurityProvider = ({ children }) => {
  const [status, setStatus] = useState(null); // {pin_set, auto_lock_minutes, members}
  const [unlocked, setUnlocked] = useState(false);
  const [pinSetup, setPinSetup] = useState(false);
  const [member, setMember] = useState(null);
  const unlockedRef = useRef(false);
  unlockedRef.current = unlocked;

  const lockNow = useCallback(() => {
    setMember(null);
    setUnlocked(false);
    setPinSetup(false);
    lockVault().catch((e) => logError("lock failed", e));
    setSessionHeader(null);
    try { navigator.serviceWorker?.controller?.postMessage({ type: "hv-purge-docs" }); } catch (e) { /* no sw */ }
  }, []);

  const refreshStatus = useCallback(async () => {
    const s = await securityStatus();
    if (!s.pin_set) {
      setMember(IMPLICIT_OWNER);
      setUnlocked(true); // onboarding: no members yet → open
      setStatus(s);
      return s;
    }
    try {
      const me = await fetchMe(); // succeeds only with a valid session cookie / in-memory token
      setMember(me);
      setUnlocked(true);
    } catch {
      setSessionHeader(null);
      setMember(null);
      setUnlocked(false);
    }
    setStatus(s); // only after the session check, so gates never see "locked" for a signed-in member
    return s;
  }, []);

  useEffect(() => {
    refreshStatus().catch((e) => logError("security status failed", e));
  }, [refreshStatus]);

  // Server rejected the session (expired / member removed) → lock the UI
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        // /api/auth/* 401s mean "no Google session", not an expired vault unlock
        const url = err?.config?.url || "";
        if (err?.response?.status === 401 && unlockedRef.current && !url.includes("/api/auth/")) {
          setSessionHeader(null);
          setMember(null);
          setUnlocked(false);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const finishUnlock = useCallback((token, m) => {
    setSessionHeader(token);
    setMember(m);
    setUnlocked(true);
  }, []);

  const doUnlock = useCallback(async (pin, memberId = null) => {
    const res = await unlockVault(pin, memberId);
    if (res.requires_otp || res.requires_passkey) return res;
    finishUnlock(res.token, res.member);
    return res;
  }, [finishUnlock]);

  const doUnlockOtp = useCallback(async (challengeId, code) => {
    const res = await unlockWithOtp(challengeId, code);
    finishUnlock(res.token, res.member);
    if (res.pin_reset_allowed) setPinSetup(true);
  }, [finishUnlock]);

  const doUnlockPasskey = useCallback(async (pending) => {
    const { startAuthentication } = await import("@simplewebauthn/browser");
    const { passkeyAuthOptions, passkeyAuthVerify } = await import("../lib/api");
    const options = await passkeyAuthOptions(pending);
    const cred = await startAuthentication({ optionsJSON: options });
    const { token, member: m } = await passkeyAuthVerify(cred, pending);
    finishUnlock(token, m);
  }, [finishUnlock]);

  const doUnlockAdminPasskey = useCallback(async () => {
    const { startAuthentication } = await import("@simplewebauthn/browser");
    const { adminPasskeyOptions, adminPasskeyVerify } = await import("../lib/api");
    const options = await adminPasskeyOptions();
    const cred = await startAuthentication({ optionsJSON: options });
    const { token, member: m } = await adminPasskeyVerify(cred);
    finishUnlock(token, m);
  }, [finishUnlock]);

  const createPin = useCallback(
    async (pin, name = "Owner", setupCode = "") => {
      const { token, member: m } = await apiSetPin(pin, name, setupCode);
      setSessionHeader(token);
      setMember(m);
      setUnlocked(true);
      await refreshStatus();
    },
    [refreshStatus]
  );

  const recoverPin = useCallback(
    async (resetCode, newPin) => {
      const { token, member: m } = await recoverOwnerPin(resetCode, newPin);
      setSessionHeader(token);
      setMember(m);
      setUnlocked(true);
      await refreshStatus();
    },
    [refreshStatus]
  );

  useIdleLock(Boolean(status?.pin_set && unlocked), status?.auto_lock_minutes, lockNow);

  const role = member?.role || (status && !status.pin_set ? "owner" : null);
  const value = {
    status,
    unlocked,
    member,
    finishUnlock,
    pinSetup,
    clearPinSetup: () => setPinSetup(false),
    role,
    isOwner: role === "owner" || role === "admin",
    isAdmin: role === "admin",
    adminAccess: member?.admin_access || (role === "admin" ? { allowed: true, reason: "admin" } : role === "owner" ? { allowed: true, reason: "bootstrap" } : { allowed: false, reason: "role" }),
    canEdit: role === "owner" || role === "partner" || role === "admin",
    readOnly: role === "executor",
    lockNow,
    doUnlock,
    doUnlockOtp,
    doUnlockPasskey,
    doUnlockAdminPasskey,
    createPin,
    recoverPin,
    refreshStatus,
  };

  return <SecurityContext.Provider value={value}>{children}</SecurityContext.Provider>;
};
